/**
 * AgentEmptyState — Mechagora Agent brand face for empty / waiting / offline / error surfaces.
 *
 * CHARACTER.md: product empty states use the same Agent character poses (no letter M).
 * Agent M lockup (`logo-agent-m.*`) is navbar / chrome only — never used here.
 * Do not substitute emoji robots, Lucide/Ant robot icons, or stock androids.
 * PRODUCT: prove don't costume — no stub zeros; waiting/error/offline must read clearly.
 */

'use client'

import React from 'react'
import Image from 'next/image'
import { EnterpriseSpinner } from '@/shared/components/feedback/EnterpriseSpinner'
import styles from './AgentEmptyState.module.css'

/** Landing/product character poses — never logo-agent-m (navbar only). */
export type AgentEmptyPose = 'workspace' | 'landing' | 'access' | 'cta'

export type AgentEmptyStateVariant = 'empty' | 'loading' | 'offline' | 'error'

export interface AgentEmptyStateAction {
  label: string
  onClick: () => void
  tone?: 'primary' | 'secondary'
  disabled?: boolean
}

export interface AgentEmptyStateProps {
  variant?: AgentEmptyStateVariant
  title: string
  description?: string
  /**
   * Character pose (no M). Defaults:
   * - empty → workspace (desk operator)
   * - loading → landing (ready stance)
   * - offline / error → access (calm hold)
   */
  pose?: AgentEmptyPose
  actions?: AgentEmptyStateAction[]
  compact?: boolean
  className?: string
  /** Desk surfaces use flagship tokens; default keeps the ivory empty well. */
  appearance?: 'default' | 'desk'
  'aria-label'?: string
}

const POSE_SRC: Record<AgentEmptyPose, string> = {
  workspace: '/assets/brand/agent-workspace.png',
  landing: '/assets/brand/agent-landing.png',
  access: '/assets/brand/agent-access.png',
  cta: '/assets/brand/agent-cta.png',
}

const DEFAULT_POSE: Record<AgentEmptyStateVariant, AgentEmptyPose> = {
  empty: 'workspace',
  loading: 'landing',
  offline: 'access',
  error: 'access',
}

/**
 * Calm empty / loading / offline / error surface with the Mechagora Agent as the brand face.
 * Shared by AI trading and other agent supervision panels.
 */
export function AgentEmptyState({
  variant = 'empty',
  title,
  description,
  pose,
  actions = [],
  compact = false,
  className = '',
  appearance = 'default',
  'aria-label': ariaLabel,
}: AgentEmptyStateProps) {
  const resolvedPose = pose ?? DEFAULT_POSE[variant]
  const src = POSE_SRC[resolvedPose]
  const isLoading = variant === 'loading'
  const isError = variant === 'error'
  const isOffline = variant === 'offline'
  const label = ariaLabel || title
  const role = isError ? 'alert' : 'status'

  return (
    <div
      className={[
        styles.root,
        compact ? styles.compact : '',
        isError ? styles.error : '',
        isOffline ? styles.offline : '',
        appearance === 'desk' ? styles.desk : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role={role}
      aria-live={isLoading || isOffline ? 'polite' : undefined}
      aria-busy={isLoading ? true : undefined}
      aria-label={label}
      data-agent-empty={variant}
      data-agent-pose={resolvedPose}
    >
      <div className={styles.face} aria-hidden="true">
        <div className={styles.facePlate}>
          <Image
            src={src}
            alt=""
            width={compact ? 96 : 128}
            height={compact ? 96 : 128}
            className={styles.faceImg}
            priority={false}
          />
        </div>
        {isLoading ? (
          <div className={styles.loadingCue}>
            <EnterpriseSpinner size="sm" variant="primary" aria-label={title} />
          </div>
        ) : null}
      </div>

      {(isOffline || isError) && (
        <p className={styles.statusChip} data-tone={isError ? 'error' : 'offline'}>
          {isError ? 'Error' : 'Offline'}
        </p>
      )}

      <p className={styles.title}>{title}</p>

      {description ? <p className={styles.description}>{description}</p> : null}

      {actions.length > 0 ? (
        <div className={styles.actions}>
          {actions.map((action) => {
            const defaultTone = isError ? 'primary' : 'secondary'
            const tone = action.tone || defaultTone
            const btnClass =
              tone === 'primary'
                ? `${styles.action} ${styles.actionPrimary}`
                : `${styles.action} ${styles.actionSecondary}`
            return (
              <button
                key={action.label}
                type="button"
                className={btnClass}
                onClick={action.onClick}
                disabled={action.disabled}
              >
                {action.label}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

export default AgentEmptyState
